import * as ReviewDeck from "../utils/ReviewDeck.js";
import * as Passport from "../utils/Passport.js";
import ArrayUtils from "../utils/ArrayUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { zoneOf } from "../utils/Continents.js";
import { bindAnswerKeys } from "../utils/QuizKeyboard.js";

/** Cartes par séance : assez pour progresser, assez peu pour revenir demain. */
const SESSION_SIZE = 20;

const escapeHtml = (s) => String(s)
	.replace(/&/g, "&amp;")
	.replace(/</g, "&lt;")
	.replace(/>/g, "&gt;")
	.replace(/"/g, "&quot;");

/**
 * Page /revision (/en/review) : la séance de révision espacée (17 du TODO).
 * Un drapeau à nommer ou une capitale à retrouver, en QCM, avec des
 * propositions du même continent ; la bonne réponse est montrée après une
 * erreur, c'est le moment où elle se retient.
 */
export default class ReviewPage {
	constructor() {
		this.page = document.querySelector(".page.review");
		if (!this.page) return;
		this.app = window.geoNerdApp;
		this.t = (k) => this.app.languageManager.t(k);
		this.body = this.page.querySelector(".review-body");
		this.byCode = new Map(this.app.countries.map(c => [c.code, c]));
		this.renderOverview();
	}

	cardsDue() {
		return ReviewDeck.dueCards().filter(card => this.byCode.has(card.code));
	}

	formatNext(ms) {
		const lang = this.app.languageManager.currentLang === "en" ? "en-GB" : "fr-FR";
		return new Date(ms).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });
	}

	renderOverview() {
		const stats = ReviewDeck.stats();
		const due = this.cardsDue().length;
		const prefix = this.app.languageManager.currentLang === "en" ? "/en" : "";
		const tiles = `
			<div class="review-stats">
				<div><strong>${due}</strong><span>${this.t("review.statDue")}</span></div>
				<div><strong>${stats.learning}</strong><span>${this.t("review.statLearning")}</span></div>
				<div><strong>${stats.mastered}</strong><span>${this.t("review.statMastered")}</span></div>
			</div>`;

		if (due > 0) {
			this.body.innerHTML = `
				${tiles}
				<button type="button" class="btn review-start">${this.t("review.start").replace("{n}", Math.min(due, SESSION_SIZE))}</button>`;
			this.body.querySelector(".review-start").addEventListener("click", () => this.startSession());
			return;
		}
		const empty = stats.learning + stats.mastered === 0
			? this.t("review.emptyDeck")
			: stats.next ? this.t("review.nothingDue").replace("{date}", this.formatNext(stats.next)) : this.t("review.allMastered");
		this.body.innerHTML = `
			${tiles}
			<p class="review-empty">${empty}</p>
			<div class="review-links">
				<a class="btn" href="${prefix}/flag-nerd-classic">${this.t("home.flagNerd")}</a>
				<a class="btn btn-secondary" href="${prefix}/capital-nerd-classic">${this.t("home.capitalNerd")}</a>
			</div>`;
	}

	startSession() {
		this.queue = ArrayUtils.shuffle(this.cardsDue().slice(0, SESSION_SIZE));
		this.index = 0;
		this.correct = 0;
		this.body.innerHTML = `
			<div class="review-progress"><span class="review-count"></span></div>
			<div class="review-question"></div>
			<div class="review-answers answer-container"></div>
			<p class="review-feedback" aria-live="polite"></p>`;
		this.answers = this.body.querySelector(".review-answers");
		this.unbindKeys = bindAnswerKeys(this.answers);
		this.nextCard();
	}

	/** Trois distracteurs, du même continent quand c'est possible. */
	proposals(card) {
		const right = this.byCode.get(card.code);
		const zone = zoneOf(card.code);
		const same = this.app.countries.filter(c => c.code !== card.code && zoneOf(c.code) === zone);
		const pool = same.length >= 3 ? same : this.app.countries.filter(c => c.code !== card.code);
		const label = (c) => (card.skill === "capital" ? c.capital : c.name);
		const picks = [];
		for (const c of ArrayUtils.shuffle([...pool])) {
			if (picks.length === 3) break;
			if (label(c) !== label(right) && !picks.some(p => label(p) === label(c))) picks.push(c);
		}
		return ArrayUtils.shuffle([right, ...picks]).map(c => ({ code: c.code, label: label(c) }));
	}

	nextCard() {
		if (this.index >= this.queue.length) {
			this.finish();
			return;
		}
		const card = this.queue[this.index];
		const country = this.byCode.get(card.code);
		this.body.querySelector(".review-count").textContent = `${this.index + 1} / ${this.queue.length}`;
		this.body.querySelector(".review-feedback").textContent = "";
		const flag = `<div class="flag" style="background-image: url(${getFlagPath(card.code)})"></div>`;
		this.body.querySelector(".review-question").innerHTML = card.skill === "capital"
			? `${flag}<p class="review-prompt"><strong>${escapeHtml(country.name)}</strong><br>${this.t("daily.capitalQuestion")}</p>`
			: `${flag}<p class="review-prompt">${this.t("review.flagQuestion")}</p>`;

		this.answered = false;
		this.answers.innerHTML = this.proposals(card)
			.map(p => `<button type="button" class="country" data-code="${p.code}">${escapeHtml(p.label)}</button>`)
			.join("");
		this.answers.querySelectorAll(".country").forEach(btn => btn.addEventListener("click", () => this.answer(card, btn)));
	}

	answer(card, btn) {
		if (this.answered) return;
		this.answered = true;
		const correct = btn.dataset.code === card.code;
		ReviewDeck.recordReview(card.skill, card.code, correct);
		if (correct) {
			this.correct++;
			Passport.stamp(card.code, card.skill);
			SoundManager.correct();
			FeedbackEffects.confetti(btn);
			btn.classList.add("valid");
		} else {
			SoundManager.wrong();
			btn.classList.add("invalid");
			this.answers.querySelector(`[data-code="${card.code}"]`)?.classList.add("valid");
			const country = this.byCode.get(card.code);
			this.body.querySelector(".review-feedback").textContent = this.t("review.itWas")
				.replace("{answer}", card.skill === "capital" ? country.capital : country.name);
		}
		setTimeout(() => {
			this.index++;
			this.nextCard();
		}, correct ? 700 : 1600);
	}

	finish() {
		if (this.unbindKeys) this.unbindKeys();
		const stats = ReviewDeck.stats();
		const next = stats.due > 0
			? this.t("review.moreDue").replace("{n}", stats.due)
			: stats.next ? this.t("review.nextOn").replace("{date}", this.formatNext(stats.next)) : this.t("review.allMastered");
		this.body.innerHTML = `
			<p class="review-done"><strong>${this.correct}/${this.queue.length}</strong> ${this.t("review.retained")}</p>
			<p class="review-next">${next}</p>
			<button type="button" class="btn review-again">${this.t("review.backToOverview")}</button>`;
		this.body.querySelector(".review-again").addEventListener("click", () => this.renderOverview());
	}
}
